import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { Layers, Eye, EyeOff, Gift } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get('ref') || '').trim().toUpperCase();
  const [isRegister, setIsRegister] = useState(!!refFromUrl);  // auto-open register if ?ref= is present
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState(refFromUrl || localStorage.getItem('xlabelsync_ref') || '');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (refFromUrl) {
      localStorage.setItem('xlabelsync_ref', refFromUrl);
      // Track the click (best-effort, no auth needed)
      axios.post(`${API}/api/referrals/click`, { code: refFromUrl }).catch(() => {});
    }
  }, [refFromUrl]);

  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    first_name: '', last_name: '', username: '', mobile_no: ''
  });

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        if (form.password !== form.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        await register({
          email: form.email, password: form.password,
          first_name: form.first_name, last_name: form.last_name,
          username: form.username, mobile_no: form.mobile_no,
          ref: referralCode || undefined,
        });
        // Clean up the stored ref after successful registration
        localStorage.removeItem('xlabelsync_ref');
      } else {
        await login(form.email, form.password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 bg-[#0F172A] p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0052FF 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold text-lg text-white">XLabelSync</span>
          </Link>
        </div>
        <div className="relative z-10">
          <h2 className="font-cabinet text-4xl font-black text-white tracking-tighter leading-tight mb-4">
            Print labels,<br />sync products,<br />grow your business.
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Join thousands of retail businesses using XLabelSync for label printing and inventory management.
          </p>
        </div>
        <div className="relative z-10">
          <p className="text-sm text-slate-500">&copy; 2025 XLabelSync</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
                <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
              </div>
              <span className="font-bold text-lg text-[#0F172A]">XLabelSync</span>
            </Link>
          </div>

          <h1 className="text-2xl font-bold text-[#0F172A] mb-1">
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            {isRegister ? 'Start your free trial today' : 'Log in to your account'}
          </p>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          {isRegister && referralCode && (
            <div className="mb-5 p-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-2" data-testid="referral-banner">
              <Gift className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-green-800">You&rsquo;ve been invited!</p>
                <p className="text-xs text-green-700 mt-0.5">
                  Referral code <span className="font-mono font-bold">{referralCode}</span> &middot; You&rsquo;ll both get <strong>30 free days</strong> when you subscribe.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setReferralCode(''); localStorage.removeItem('xlabelsync_ref'); }}
                className="text-xs text-green-700 hover:text-green-900 font-semibold"
                data-testid="referral-clear"
              >Clear</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">First Name</label>
                    <input name="first_name" value={form.first_name} onChange={handleChange} required
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                      data-testid="register-first-name" placeholder="John" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Last Name</label>
                    <input name="last_name" value={form.last_name} onChange={handleChange} required
                      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                      data-testid="register-last-name" placeholder="Doe" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Username</label>
                  <input name="username" value={form.username} onChange={handleChange} required
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                    data-testid="register-username" placeholder="johndoe" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Phone Number</label>
                  <input name="mobile_no" value={form.mobile_no} onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                    data-testid="register-phone" placeholder="+1 (555) 123-4567" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                data-testid="login-email" placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Password</label>
              <div className="relative">
                <input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 pr-10 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="login-password" placeholder="Enter your password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Confirm Password</label>
                <input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="register-confirm-password" placeholder="Confirm your password" />
              </div>
            )}

            {!isRegister && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm font-medium text-[#0052FF] hover:text-[#0040CC]" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Log in'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-[#0052FF] font-medium hover:text-[#0040CC]" data-testid="toggle-auth-mode">
              {isRegister ? 'Log in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
